/**
 * Quantity Selector Component
 * 
 * Reusable quantity selector with increment/decrement buttons and direct input
 */

import { __ } from '@wordpress/i18n';

const QuantitySelector = ({
    quantity = 0,
    maxQuantity = 99,
    minQuantity = 0,
    onChange,
    disabled = false,
    showQuickSelect = true,
    quickSelectValues = [1, 5, 10, 20, 50],
    showMaxLabel = true,
    title = null,
    className = '',
    size = 'normal', // 'small', 'normal', 'large'
    isMobile = false,
    style = {}
}) => {
    /**
     * Handle quantity input change
     */
    const handleQuantityInputChange = (value) => {
        let qty = parseInt(value) || 0;
        qty = Math.max(minQuantity, Math.min(qty, maxQuantity));
        onChange(qty);
    };
    
    /**
     * Handle quantity button changes
     */
    const handleQuantityButtonChange = (increment) => {
        const newQty = Math.max(minQuantity, Math.min(quantity + increment, maxQuantity));
        onChange(newQty);
    };
    
    // Size configurations
    const sizeConfigs = {
        small: {
            buttonSize: '32px',
            inputWidth: '80px',
            inputHeight: '32px',
            fontSize: '14px',
            gap: '8px'
        },
        normal: {
            buttonSize: '40px',
            inputWidth: '100px',
            inputHeight: '40px',
            fontSize: '16px',
            gap: '12px'
        },
        large: {
            buttonSize: '48px',
            inputWidth: '120px',
            inputHeight: '48px',
            fontSize: '18px',
            gap: '16px'
        }
    };
    
    const config = sizeConfigs[size] || sizeConfigs.normal;
    
    // Apply mobile adjustments
    if (isMobile && size === 'normal') {
        config.fontSize = '16px'; // Prevent zoom on iOS
    }
    
    return (
        <div className={`fos-quantity-selector ${className}`} style={style}>
            {/* Title */}
            {title && (
                <h4 className="fos-quantity-title" style={{ 
                    margin: '0 0 12px 0',
                    fontSize: size === 'small' ? '1rem' : '1.1rem',
                    color: '#333'
                }}>
                    {title}
                </h4>
            )}
            
            {/* Main Quantity Controls */}
            <div className="fos-quantity-controls" style={{
                display: 'flex',
                alignItems: 'center',
                gap: config.gap,
                padding: size === 'small' ? '12px' : '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e1e1e1',
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto'
            }}>
                {/* Decrease Button */}
                <button
                    type="button"
                    className="fos-quantity-btn fos-quantity-decrease"
                    onClick={() => handleQuantityButtonChange(-1)}
                    disabled={disabled || quantity <= minQuantity}
                    style={{
                        width: config.buttonSize,
                        height: config.buttonSize,
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        background: (disabled || quantity <= minQuantity) ? '#f5f5f5' : '#fff',
                        color: (disabled || quantity <= minQuantity) ? '#999' : '#333',
                        fontSize: config.fontSize,
                        cursor: (disabled || quantity <= minQuantity) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                    }}
                    aria-label={__('減少數量', 'frontend-order-system')}
                    onMouseEnter={(e) => {
                        if (!disabled && quantity > minQuantity) {
                            e.target.style.background = '#f0f0f0';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!disabled && quantity > minQuantity) {
                            e.target.style.background = '#fff';
                        }
                    }}
                >
                    −
                </button>
                
                {/* Quantity Input */}
                <input
                    type="number"
                    className="fos-quantity-input"
                    min={minQuantity}
                    max={maxQuantity}
                    value={quantity || ''}
                    onChange={(e) => handleQuantityInputChange(e.target.value)}
                    disabled={disabled}
                    placeholder={minQuantity.toString()}
                    style={{
                        width: config.inputWidth,
                        height: config.inputHeight,
                        textAlign: 'center',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: config.fontSize,
                        padding: '0 8px',
                        background: disabled ? '#f5f5f5' : '#fff'
                    }}
                    aria-label={__('輸入數量', 'frontend-order-system')}
                />
                
                {/* Increase Button */}
                <button
                    type="button"
                    className="fos-quantity-btn fos-quantity-increase"
                    onClick={() => handleQuantityButtonChange(1)}
                    disabled={disabled || quantity >= maxQuantity}
                    style={{
                        width: config.buttonSize,
                        height: config.buttonSize,
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        background: (disabled || quantity >= maxQuantity) ? '#f5f5f5' : '#fff',
                        color: (disabled || quantity >= maxQuantity) ? '#999' : '#333',
                        fontSize: config.fontSize,
                        cursor: (disabled || quantity >= maxQuantity) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                    }}
                    aria-label={__('增加數量', 'frontend-order-system')}
                    onMouseEnter={(e) => {
                        if (!disabled && quantity < maxQuantity) {
                            e.target.style.background = '#f0f0f0';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!disabled && quantity < maxQuantity) {
                            e.target.style.background = '#fff';
                        }
                    }}
                >
                    +
                </button>
                
                {/* Clear Button in place of Max Label */}
                {quantity > minQuantity && (
                    <button
                        type="button"
                        className="fos-btn-secondary fos-clear-btn"
                        style={{
                            padding: size === 'small' ? '4px 8px' : '6px 12px',
                            fontSize: size === 'small' ? '0.75rem' : '0.8rem',
                            minHeight: 'auto',
                            background: '#f8d7da',
                            color: '#721c24',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.6 : 1,
                            transition: 'all 0.2s ease',
                            marginLeft: '8px',
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => onChange(minQuantity)}
                        disabled={disabled}
                    >
                        {__('清除', 'frontend-order-system')}
                    </button>
                )}
                
                {/* Max Label (hidden but functionality preserved) */}
                {showMaxLabel && (
                    <span style={{ 
                        fontSize: size === 'small' ? '0.8rem' : '0.9rem', 
                        color: '#666',
                        marginLeft: '8px',
                        whiteSpace: 'nowrap'
                    }}>
                        {__('最大:', 'frontend-order-system')} {maxQuantity}
                    </span>
                )}
            </div>
            
            {/* Quick Select Buttons */}
            {showQuickSelect && quickSelectValues.length > 0 && (
                <div className="fos-quick-select" style={{ marginTop: '16px' }}>
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: size === 'small' ? '0.8rem' : '0.9rem',
                        color: '#666'
                    }}>
                        {__('快速選擇:', 'frontend-order-system')}
                    </label>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {quickSelectValues
                            .filter(quickQty => quickQty <= maxQuantity && quickQty >= minQuantity)
                            .map(quickQty => (
                                <button
                                    key={quickQty}
                                    type="button"
                                    className="fos-btn-secondary fos-quick-select-btn"
                                    style={{
                                        padding: size === 'small' ? '4px 8px' : '6px 12px',
                                        fontSize: size === 'small' ? '0.75rem' : '0.8rem',
                                        minHeight: 'auto',
                                        background: quantity === quickQty ? '#0073aa' : '#f0f0f1',
                                        color: quantity === quickQty ? '#fff' : '#333',
                                        border: quantity === quickQty ? '1px solid #0073aa' : '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.6 : 1,
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => onChange(quickQty)}
                                    disabled={disabled}
                                >
                                    {quickQty}
                                </button>
                            ))}
                        
                    </div>
                </div>
            )}
            
        </div>
    );
};

export default QuantitySelector;